import { useState, useMemo, useCallback, useEffect } from "react";
import { useFiltersContext } from "../../../../../pages/Workspace/UserFilters/FiltersContext";
import { GlobalPresenceOption, LocationTypeOption } from "../constants";
import { InclusionMethodValue } from "../../../../../legacy/types/companyFilter";

export const useGlobalPresenceFilter = () => {
  const { draftFilters, setDraftFilters } = useFiltersContext();

  // Determine initial option based on filter values
  const initialOption = useMemo(() => {
    if (!draftFilters?.globalPresence) {
      return GlobalPresenceOption.NONE;
    }

    if (draftFilters.globalPresence.isMultiNational === true) {
      return GlobalPresenceOption.MULTI_NATIONAL;
    }

    if (
      draftFilters.globalPresence.isMultiNational === false &&
      (draftFilters.globalPresence.hqCountryCodes ||
        draftFilters.globalPresence.hqRegions ||
        draftFilters.globalPresence.subsidiaryLocations)
    ) {
      return GlobalPresenceOption.BY_LOCATION;
    }

    return GlobalPresenceOption.NONE;
  }, [draftFilters?.globalPresence]);

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [showHQSelector, setShowHQSelector] = useState(
    !!draftFilters?.globalPresence?.hqCountryCodes ||
      !!draftFilters?.globalPresence?.hqRegions
  );
  const [showSubsidiarySelector, setShowSubsidiarySelector] = useState(
    !!draftFilters?.globalPresence?.subsidiaryLocations
  );

  // HQ state
  const [hqLocationType, setHqLocationType] = useState<LocationTypeOption>(
    draftFilters?.globalPresence?.hqCountryCodes
      ? LocationTypeOption.COUNTRY
      : draftFilters?.globalPresence?.hqRegions
        ? LocationTypeOption.REGION
        : LocationTypeOption.COUNTRY
  );
  const [hqLocationValues, setHqLocationValues] = useState<string[]>(
    draftFilters?.globalPresence?.hqCountryCodes ||
      draftFilters?.globalPresence?.hqRegions ||
      []
  );

  // Subsidiary state
  const [subsidiaryLocationType, setSubsidiaryLocationType] =
    useState<LocationTypeOption>(
      draftFilters?.globalPresence?.subsidiaryLocations?.countryCodes
        ? LocationTypeOption.COUNTRY
        : draftFilters?.globalPresence?.subsidiaryLocations?.regions
          ? LocationTypeOption.REGION
          : LocationTypeOption.COUNTRY
    );
  const [subsidiaryLocationValues, setSubsidiaryLocationValues] = useState<
    string[]
  >(
    draftFilters?.globalPresence?.subsidiaryLocations?.countryCodes ||
      draftFilters?.globalPresence?.subsidiaryLocations?.regions ||
      []
  );
  const [subsidiaryMatchType, setSubsidiaryMatchType] = useState<string>(
    draftFilters?.globalPresence?.subsidiaryLocations?.inclusionMethod ===
      "ALL_PRESENT"
      ? "ALL"
      : draftFilters?.globalPresence?.subsidiaryLocations?.countMatching
        ? String(
            draftFilters?.globalPresence?.subsidiaryLocations?.countMatching
          )
        : "ANY"
  );

  const updateGlobalPresenceFilter = useCallback(
    (option: GlobalPresenceOption) => {
      switch (option) {
        case GlobalPresenceOption.NONE:
          setDraftFilters((prevDraftFilters) => ({
            ...prevDraftFilters,
            globalPresence: undefined,
          }));
          break;

        case GlobalPresenceOption.MULTI_NATIONAL:
          setDraftFilters((prevDraftFilters) => ({
            ...prevDraftFilters,
            globalPresence: {
              isMultiNational: true,
              subsidiaryLocations: undefined,
              hqCountryCodes: undefined,
              hqRegions: undefined,
            },
          }));
          break;

        case GlobalPresenceOption.BY_LOCATION:
          setDraftFilters((prevDraftFilters) => ({
            ...prevDraftFilters,
            globalPresence: {
              isMultiNational: false,
              subsidiaryLocations: undefined,
              hqCountryCodes: undefined,
              hqRegions: undefined,
            },
          }));
          break;
      }
    },
    [setDraftFilters]
  );

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value as GlobalPresenceOption;
      setSelectedOption(newValue);

      // Update draftFilters based on selection
      updateGlobalPresenceFilter(newValue);

      if (newValue !== GlobalPresenceOption.BY_LOCATION) {
        setShowHQSelector(false);
        setShowSubsidiarySelector(false);
      }
    },
    [updateGlobalPresenceFilter, setShowHQSelector, setShowSubsidiarySelector]
  );

  const handleAddHQLocations = useCallback(() => setShowHQSelector(true), []);

  const handleAddSubsidiaryLocations = useCallback(
    () => setShowSubsidiarySelector(true),
    []
  );

  const handleHQLocationTypeChange = useCallback(
    (locationType: LocationTypeOption) => {
      setHqLocationType(locationType);
      setHqLocationValues([]);

      // Update filters with new location type
      setDraftFilters((prevDraftFilters) => ({
        ...prevDraftFilters,
        globalPresence: {
          ...prevDraftFilters?.globalPresence,
          isMultiNational: false,
          hqCountryCodes:
            locationType === LocationTypeOption.COUNTRY ? [] : undefined,
          hqRegions:
            locationType === LocationTypeOption.REGION ? [] : undefined,
        },
      }));
    },
    [setDraftFilters]
  );

  const handleHQLocationValuesChange = useCallback(
    (values: string[]) => {
      setHqLocationValues(values);

      if (values.length === 0) {
        setDraftFilters((prevDraftFilters) => ({
          ...prevDraftFilters,
          globalPresence: {
            ...prevDraftFilters?.globalPresence,
            isMultiNational: false,
            hqCountryCodes: undefined,
          },
        }));
        return;
      }

      // Update filters with new values
      setDraftFilters((prevDraftFilters) => ({
        ...prevDraftFilters,
        globalPresence: {
          ...prevDraftFilters?.globalPresence,
          isMultiNational: false,
          hqCountryCodes:
            hqLocationType === LocationTypeOption.COUNTRY ? values : undefined,
          hqRegions:
            hqLocationType === LocationTypeOption.REGION ? values : undefined,
        },
      }));
    },
    [hqLocationType, setDraftFilters]
  );

  const handleSubsidiaryLocationTypeChange = useCallback(
    (locationType: LocationTypeOption) => {
      setSubsidiaryLocationType(locationType);
      setSubsidiaryLocationValues([]);

      // Update filters with new location type
      setDraftFilters((prevDraftFilters) => {
        const currentSubsidiaryLocations =
          prevDraftFilters?.globalPresence?.subsidiaryLocations;

        return {
          ...prevDraftFilters,
          globalPresence: {
            ...prevDraftFilters?.globalPresence,
            isMultiNational: false,
            subsidiaryLocations: {
              countryCodes:
                locationType === LocationTypeOption.COUNTRY ? [] : undefined,
              regions:
                locationType === LocationTypeOption.REGION ? [] : undefined,
              inclusionMethod:
                currentSubsidiaryLocations?.inclusionMethod || "ANY_PRESENT",
              countMatching: currentSubsidiaryLocations?.countMatching || null,
            },
          },
        };
      });
    },
    [setDraftFilters]
  );

  const handleSubsidiaryLocationValuesChange = useCallback(
    (values: string[]) => {
      setSubsidiaryLocationValues(values);

      if (values.length === 0) {
        setDraftFilters((prevDraftFilters) => ({
          ...prevDraftFilters,
          globalPresence: {
            ...prevDraftFilters?.globalPresence,
            isMultiNational: false,
            subsidiaryLocations: undefined,
          },
        }));
        return;
      }

      // Update filters with new values
      setDraftFilters((prevDraftFilters) => {
        const currentSubsidiaryLocations =
          prevDraftFilters?.globalPresence?.subsidiaryLocations;

        return {
          ...prevDraftFilters,
          globalPresence: {
            ...prevDraftFilters?.globalPresence,
            isMultiNational: false,
            subsidiaryLocations: {
              ...currentSubsidiaryLocations,
              countryCodes:
                subsidiaryLocationType === LocationTypeOption.COUNTRY
                  ? values
                  : undefined,
              regions:
                subsidiaryLocationType === LocationTypeOption.REGION
                  ? values
                  : undefined,
              inclusionMethod:
                currentSubsidiaryLocations?.inclusionMethod || "ANY_PRESENT",
              countMatching: currentSubsidiaryLocations?.countMatching || null,
            },
          },
        };
      });
    },
    [subsidiaryLocationType, setDraftFilters]
  );

  const handleSubsidiaryMatchTypeChange = useCallback(
    (matchType: string) => {
      setSubsidiaryMatchType(matchType);

      // Update filters with new match type
      setDraftFilters((prevDraftFilters) => {
        const currentSubsidiaryLocations =
          prevDraftFilters?.globalPresence?.subsidiaryLocations;

        let inclusionMethod: InclusionMethodValue;
        let countMatching: number | null = null;

        if (matchType === "ANY") {
          inclusionMethod = "ANY_PRESENT";
        } else if (matchType === "ALL") {
          inclusionMethod = "ALL_PRESENT";
        } else {
          inclusionMethod = "ANY_PRESENT";
          countMatching = parseInt(matchType, 10);
        }

        return {
          ...prevDraftFilters,
          globalPresence: {
            ...prevDraftFilters?.globalPresence,
            isMultiNational: false,
            subsidiaryLocations: {
              ...currentSubsidiaryLocations,
              inclusionMethod,
              countMatching,
            },
          },
        };
      });
    },
    [setDraftFilters]
  );

  const handleCloseHQSelector = useCallback(() => {
    setShowHQSelector(false);

    // Remove HQ location from filters
    setDraftFilters((prevDraftFilters) => ({
      ...prevDraftFilters,
      globalPresence: {
        ...prevDraftFilters?.globalPresence,
        isMultiNational: false,
        hqCountryCodes: undefined,
        hqRegions: undefined,
      },
    }));
  }, [setDraftFilters]);

  const handleCloseSubsidiarySelector = useCallback(() => {
    setShowSubsidiarySelector(false);

    // Remove subsidiary location from filters
    setDraftFilters((prevDraftFilters) => ({
      ...prevDraftFilters,
      globalPresence: {
        ...prevDraftFilters?.globalPresence,
        isMultiNational: false,
        subsidiaryLocations: undefined,
      },
    }));
  }, [setDraftFilters]);

  const handleClearFilter = useCallback(() => {
    setSelectedOption(GlobalPresenceOption.NONE);
    setShowHQSelector(false);
    setShowSubsidiarySelector(false);
    //clear the local states
    setHqLocationValues([]);
    setSubsidiaryLocationValues([]);

    // Clear filter
    setDraftFilters((prevDraftFilters) => ({
      ...prevDraftFilters,
      globalPresence: undefined,
    }));
  }, [setDraftFilters]);

  /***
   * currently when you click on "Clear All Filters" button,
   * it clear the global presence filter from draftFilters,
   * but here local states are not cleared,
   * so  if we have selectedOption other than NONE,
   * and draftFilters.globalPresence is undefined,
   * then we need to clear local states as well
   */
  useEffect(() => {
    if (
      (draftFilters === undefined ||
        draftFilters.globalPresence === undefined) &&
      selectedOption !== GlobalPresenceOption.NONE
    ) {
      handleClearFilter();
    }
  }, [draftFilters, handleClearFilter, selectedOption]);

  const shouldShowNote =
    selectedOption === GlobalPresenceOption.MULTI_NATIONAL ||
    selectedOption === GlobalPresenceOption.BY_LOCATION;

  return {
    selectedOption,
    showHQSelector,
    showSubsidiarySelector,
    hqLocationType,
    hqLocationValues,
    subsidiaryLocationType,
    subsidiaryLocationValues,
    subsidiaryMatchType,
    shouldShowNote,
    handleOptionChange,
    handleAddHQLocations,
    handleAddSubsidiaryLocations,
    handleHQLocationTypeChange,
    handleHQLocationValuesChange,
    handleSubsidiaryLocationTypeChange,
    handleSubsidiaryLocationValuesChange,
    handleSubsidiaryMatchTypeChange,
    handleCloseHQSelector,
    handleCloseSubsidiarySelector,
    handleClearFilter,
  };
};
